import { ChannelManager } from '../../api/ChannelManager';
import { EpgUtil } from '../utility/EpgUtil';
import { ActionMethod, AssetType, CardSize, CardType, CmsProperty, SortDir } from '../../api/models/Shared.types';
import {
  IChannelLineupV5,
  ICustomCmsRibbon,
  IRibbon2Container,
  IRibbon2Detail,
  IScheduleV5,
} from '../../api/models/AndromeTV.types';
import { ListRibbon2Asset } from './ListRibbonDataSource';
import { CardDataSource } from '../cms/CardDataSource';
import { CardSizeInfo } from '../cms/CardSizeInfo';
import { ChannelModel } from '../../api/EpgApi.types';
import { Backend } from '../../api/Backend';
import { EpgApiV5 } from '../../api/EpgApiV5';

export class MiniEpgDataSource {
  private initialized = false;
  private allChannels: ChannelModel[] = [];
  private validChannels: ChannelModel[] = [];
  private readonly backend: Backend;
  private initPromise: Promise<ChannelModel[]> | undefined;

  constructor(backend: Backend) {
    this.backend = backend;
  }

  /**
   * Load valid channels
   */
  initialize(): Promise<ChannelModel[]> {
    if (this.initPromise) {
      return this.initPromise;
    } else {
      this.initPromise = new Promise(resolve => {
        if (this.initialized) {
          resolve(this.validChannels);
        } else {
          resolve(
            ChannelManager.validChannels(this.backend, false).then(channels => {
              channels.validChannels.forEach(chan => {
                chan.findChannelNumber(this.backend);
              });

              function compare(a: ChannelModel, b: ChannelModel) {
                if (!a.channelNumber || !b.channelNumber) return 0;
                if (a.channelNumber < b.channelNumber) {
                  return -1;
                }
                if (a.channelNumber > b.channelNumber) {
                  return 1;
                }
                return 0;
              }

              channels.validChannels.sort(compare);

              const favPromisses = channels.validChannels.map(el => el.updateFavorite(this.backend));
              return Promise.all(favPromisses).then(() => {
                const favs = channels.validChannels.filter(el => el.favorite);
                const other = channels.validChannels.filter(el => !el.favorite);
                this.validChannels = favs.concat(other);
                this.allChannels = channels.allChannels;
                this.initialized = true;
                return this.validChannels;
              });
            })
          );
        }
      });
    }
    return this.initPromise;
  }

  loadCardSchedules(channelId: string) {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    const fromUntilYesterday = EpgUtil.fromUntilFullDay(yesterday);
    const fromUntilToday = EpgUtil.fromUntilFullDay(today);
    const fromUntilTomorrow = EpgUtil.fromUntilFullDay(tomorrow);

    // Note: doing 3 separate calls for yesterday, today, tomorrow for better caching
    // as opposed to a 3 day list only being usable 1 day
    // Note: using the lineup and not lineupRibbon here since lineupRibbon is a paged call
    // a paged call would require multiple subsequent calls to load the full yesterday list for example
    return Promise.all([
      EpgApiV5.channelLineup(this.backend, channelId, fromUntilYesterday.from, fromUntilYesterday.until),
      EpgApiV5.channelLineup(this.backend, channelId, fromUntilToday.from, fromUntilToday.until),
      EpgApiV5.channelLineup(this.backend, channelId, fromUntilTomorrow.from, fromUntilTomorrow.until),
    ]).then(([lineupYes, lineupTod, lineupTom]) => {
      EpgUtil.sortSchedulesByStartDate(lineupYes.schedules, SortDir.Asc);
      EpgUtil.sortSchedulesByStartDate(lineupTod.schedules, SortDir.Asc);
      EpgUtil.sortSchedulesByStartDate(lineupTom.schedules, SortDir.Asc);

      const assetsYes = lineupYes.schedules
        ?.map(schedule => this.scheduleToRibbon2Asset(schedule, lineupYes, channelId))
        .filter(schedule => schedule) as ListRibbon2Asset[];
      const assetsTod = lineupTod.schedules
        ?.map(schedule => this.scheduleToRibbon2Asset(schedule, lineupTod, channelId))
        .filter(schedule => schedule) as ListRibbon2Asset[];
      const assetsTom = lineupTom.schedules
        ?.map(schedule => this.scheduleToRibbon2Asset(schedule, lineupTom, channelId))
        .filter(schedule => schedule) as ListRibbon2Asset[];

      const cardSzi = new CardSizeInfo();
      const ribbonCont: IRibbon2Container = {
        type: CardType.Landscape,
        items: assetsYes, // only for calculation of size, no need to pass all data
      };
      const cmsProps = [
        CmsProperty.ShowScheduleInfo,
        CmsProperty.ShowProgress,
        CmsProperty.UseBestSchedule,
        CmsProperty.HideMiniDetail,
      ];
      const ribbonDat: ICustomCmsRibbon = { cardSize: CardSize.MiniEpg, url: '' };
      cardSzi.updateWithContainer(ribbonCont, cmsProps, ribbonDat);
      // add cardDses into a cache maybe
      return [...assetsYes, ...assetsTod, ...assetsTom].map(
        el => new CardDataSource(this.backend, el, cardSzi, cmsProps, this.validChannels)
      );
    });
  }

  scheduleToRibbon2Asset(
    schedule: IScheduleV5,
    scheduleForChannel: IChannelLineupV5,
    channelId: string
  ): ListRibbon2Asset | undefined {
    const program = scheduleForChannel.programs?.find(p => p.programId === schedule.programId);

    if (!program) {
      return undefined;
    }

    const resources: IRibbon2Detail[] = [];
    scheduleForChannel.programs?.forEach(program =>
      resources.push(EpgUtil.programToRibbon2Detail(program, scheduleForChannel.seasons))
    );
    // console.log('ListRibbon', this, schedule, program, scheduleForChannel);
    const season = scheduleForChannel.seasons?.find(obj => {
      return obj.seasonId === program.seasonId;
    });
    return {
      asset: {
        ...program,
        title: program.text.title,
        synopsis: program.text.shortDescription,
        image: program.image.poster,
        genres: program.genres,
        episode: program.episodeNumber,
        season: season?.seasonNumber,
        published: schedule.published,
        type: AssetType.SCHEDULE,
        id: schedule.scheduleId,
      },
      method: ActionMethod.DETAIL,
      channels: [
        {
          id: channelId,
          schedules: [
            {
              ...schedule,
              id: schedule.scheduleId,
              programId: schedule.programId,
            },
          ],
        },
      ],
      resources,
    };
  }
}
